/**
 * ScoredScale — question type 'scored-scale' (Mental Health, Dr. Rami 2026-10-28: ONE generalized scored type in
 * Epworth's family; the patient SEES the score and its band, as with Epworth).
 *
 * Catalog shape (scripts/mental_health/build_catalog_39.py):
 *   { scale: 'PHQ-9', prompt, items: [text | { text, options }], labels: [...] (shared answers, score = index),
 *     scoring: 'sum' | 'count', thresholds: [...] (count: an item counts when its score >= its threshold),
 *     max, bands: [{ max, label, high }], flags: [{ item, min, text }] }
 * Saved as ONE English answer:
 *   "PHQ-9 14/27 (Moderate) — Little interest or pleasure in doing things: Several days (1); … | FLAG: <text>"
 * A flag fires when its item's score reaches `min` (even before every item is answered); the urgent-answer table
 * keys on the flag text (utils/answerAlerts.js reads the part after FLAG_SEP), so the crisis card opens under the scale.
 * Labels live in `labels`, never `options`: the voice-memo survey fill only offers questions with options.
 *
 * s126 (Detect scales, Dr. Rami 2026-10-31 — data/scales.json): optional, absent on every s122 scale so nothing changes
 *   for them: `base` (the score of the first label; NLHQ answers score 1–7), scoring 'domain-mean-sum' (each domain is
 *   the mean of its answered items, the total is the domains added, `decimals` places), `answerKey` (one line shown
 *   above the items, TNSS), `credit` (the source line, shown small under the readout).
 */
import { useI18n } from '../../i18n'
import { FLAG_SEP } from '../../utils/answerAlerts'

const itemText = (it) => (typeof it === 'string' ? it : it?.text || '')
const itemLabels = (it, shared) => (typeof it === 'object' && Array.isArray(it?.options) ? it.options : shared)
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const baseOf = (question) => (Number.isFinite(question?.base) ? question.base : 0)

export function scaleScore(question, scores) {
  if (question.scoring === 'domain-mean-sum') {
    const doms = Array.isArray(question.domains) && question.domains.length ? question.domains : [scores.map((_, i) => i)]
    const total = doms.reduce((a, d) => {
      const vals = d.map((i) => scores[i]).filter((v) => v !== null && v !== undefined)
      return a + (vals.length ? vals.reduce((x, y) => x + y, 0) / vals.length : 0)
    }, 0)
    const k = 10 ** (Number.isFinite(question.decimals) ? question.decimals : 1)
    return Math.round(total * k) / k
  }
  const th = question.thresholds || []
  if (question.scoring === 'count') return scores.reduce((a, s, i) => a + (s !== null && s >= (th[i] ?? 1) ? 1 : 0), 0)
  return scores.reduce((a, s) => a + (s ?? 0), 0)
}

export const scaleTotalText = (question, total) =>
  (Number.isFinite(question?.decimals) ? Number(total).toFixed(question.decimals) : String(total))

export const scaleBand = (question, total) => (question.bands || []).find((b) => total <= b.max) || null

export function parseScale(value, question) {
  const items = question.items || []
  const scores = items.map(() => null)
  if (typeof value !== 'string') return scores
  const body = value.split(FLAG_SEP)[0]
  items.forEach((it, i) => {
    const m = body.match(new RegExp(`${esc(itemText(it))}: .+? \\((\\d+)\\)(?=; |$)`))
    if (m) scores[i] = Number(m[1])
  })
  return scores
}

export function formatScale(question, scores) {
  const items = question.items || []
  const shared = question.labels || []
  const done = scores.every((s) => s !== null)
  const total = scaleScore(question, scores)
  const band = scaleBand(question, total)
  const base = baseOf(question)
  const head = done
    ? `${question.scale} ${scaleTotalText(question, total)}/${question.max}${band ? ` (${band.label})` : ''}`
    : `${question.scale} (incomplete, ${scores.filter((s) => s !== null).length} of ${items.length} answered)`
  const parts = items
    .map((it, i) => (scores[i] === null ? null : `${itemText(it)}: ${itemLabels(it, shared)[scores[i] - base]} (${scores[i]})`))
    .filter(Boolean)
  const flags = (question.flags || []).filter((f) => scores[f.item] !== null && scores[f.item] >= f.min).map((f) => f.text)
  return `${head} — ${parts.join('; ')}${flags.map((f) => `${FLAG_SEP}${f}`).join('')}`
}

export default function ScoredScale({ question, value, onChange, disabled, tq, to }) {
  const { t } = useI18n()
  const items = question.items || []
  const shared = question.labels || []
  const scores = parseScale(value, question)
  const done = scores.every((s) => s !== null)
  const total = scaleScore(question, scores)
  const band = scaleBand(question, total)
  const base = baseOf(question)
  const pick = (i, v) => {
    if (disabled) return
    const next = [...scores]
    next[i] = v + base
    onChange(formatScale(question, next))
  }
  return (
    <div data-scored-scale={question.scale}>
      {question.prompt && <p className="text-[13px] text-gray-600 mb-2">{tq(question.prompt)}</p>}
      {question.answerKey && <p data-scale-answer-key className="text-[11.5px] leading-snug text-gray-500 mb-2">{tq(question.answerKey)}</p>}
      {items.map((it, i) => {
        const labels = itemLabels(it, shared)
        const cols = labels.length >= 6 ? 'grid-cols-4' : labels.length === 5 ? 'grid-cols-5' : labels.length === 4 ? 'grid-cols-4' : labels.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
        return (
          <div key={itemText(it)} className="py-2.5 border-t border-gray-100 first:border-t-0">
            <p className="text-[13px] text-gray-700 mb-1.5">{tq(itemText(it))}</p>
            <div className={`grid ${cols} gap-1`}>
              {labels.map((lab, v) => (
                <button
                  key={lab}
                  type="button"
                  disabled={disabled}
                  aria-pressed={scores[i] === v + base}
                  onClick={() => pick(i, v)}
                  className={`rounded-[10px] border px-0.5 py-1.5 min-h-[40px] ${labels.length >= 5 ? 'text-[10.5px]' : 'text-[11px]'} leading-tight font-semibold transition-colors ${
                    scores[i] === v + base ? 'bg-[#46A8F9] border-[#46A8F9] text-white' : 'bg-white border-gray-200 text-gray-600'
                  }`}
                >
                  {to(lab)}
                </button>
              ))}
            </div>
          </div>
        )
      })}
      <div data-bottom-inset="a score readout, not an action: it sticks inside the question list and its pill padding is the design" className="sticky bottom-0 mt-2 rounded-xl bg-[#15354F] text-white px-3 py-2.5 flex items-center justify-between text-[12.5px]">
        <span>{t('describe.epworthYourScore')}</span>
        <span className="flex items-center gap-1.5">
          <strong className="tabular-nums">{scaleTotalText(question, total)} / {question.max}</strong>
          {done && band && (
            <span className={`rounded-full px-2 py-0.5 text-[11.5px] font-bold ${band.high ? 'bg-red-200 text-red-900' : 'bg-white/15'}`}>
              {tq(band.label)}
            </span>
          )}
        </span>
      </div>
      {question.credit && <p data-scale-credit className="text-[10px] text-gray-400 mt-1.5">{question.credit}</p>}
    </div>
  )
}
